import { useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { TeamMember } from "../../lib/types";
import {
  CommsCompose,
  CommsTimelineList,
  filterTimeline,
  fmtTime,
  useCommsTimeline,
  type CommsFilter,
} from "../comms/CommsThread";
import HomeownerCommsStrip from "./HomeownerCommsStrip";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
//
// The deal page's Comms tab. The loader, timeline, list and compose box live in
// components/comms/CommsThread.tsx (shared with the Finance → Supplements reply
// drawer since Oct 16, 2026); this file keeps the AI-summary card and the filter
// bar and lays the pieces out exactly as before: compose, summary, filters, list.

export default function CommsTab({
  dealId,
  homeownerPhone,
  commsSummary,
  commsSummaryUpdatedAt,
  members,
  onSummaryRefresh,
}: {
  dealId: string;
  homeownerPhone: string | null;
  commsSummary: string | null;
  commsSummaryUpdatedAt: string | null;
  members: Record<string, TeamMember>;
  onSummaryRefresh: () => Promise<void>;
}) {
  const [filter, setFilter] = useState<CommsFilter>("all");
  const [refreshingSummary, setRefreshingSummary] = useState(false);
  const { timeline, counts, loading, appendMessage } = useCommsTimeline({ dealId, members });

  // Filter
  const filtered = useMemo(() => filterTimeline(timeline, filter), [timeline, filter]);

  // Refresh AI summary
  async function refreshSummary() {
    setRefreshingSummary(true);
    try {
      const { error } = await supabase.functions.invoke("comms-summarize", {
        body: { deal_id: dealId },
      });
      if (error) {
        console.error("Summary refresh failed:", error);
        alert("Summary refresh failed. The Edge Function may not be deployed yet.");
      } else {
        await onSummaryRefresh();
      }
    } catch (e) {
      console.error("Summary refresh error:", e);
    }
    setRefreshingSummary(false);
  }

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-400">
        <div className="animate-pulse">Loading communications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* The automatic texts and emails (homeowner-comms engine), Oct 20, 2026 */}
      <HomeownerCommsStrip dealId={dealId} />

      {/* Quick Compose Box */}
      <CommsCompose dealId={dealId} homeownerPhone={homeownerPhone} onSent={appendMessage} />

      {/* AI Summary Card */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-bardin-ink to-slate-700 text-white">
          <div className="flex items-center gap-2">
            <span className="text-sm">&#129302;</span>
            <span className="text-sm font-semibold">AI Summary</span>
          </div>
          <button
            onClick={refreshSummary}
            disabled={refreshingSummary}
            className="text-xs px-2.5 py-1 rounded bg-white/15 hover:bg-white/25 transition disabled:opacity-50 flex items-center gap-1"
          >
            <svg className={`w-3 h-3 ${refreshingSummary ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshingSummary ? "Generating..." : "Refresh"}
          </button>
        </div>
        <div className="p-4">
          {commsSummary ? (
            <>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{commsSummary}</p>
              {commsSummaryUpdatedAt && (
                <p className="text-xs text-slate-400 mt-2">
                  Updated {fmtTime(commsSummaryUpdatedAt)}
                </p>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-400 italic">
              {timeline.length === 0
                ? "No communications yet."
                : "No summary generated yet. Click Refresh to generate one."}
            </div>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex gap-2 flex-wrap">
        {([
          ["all", "All", counts.all],
          ["texts", "Texts", counts.texts],
          ["calls", "Calls", counts.calls],
          ["notes", "Notes & Activity", counts.notes],
        ] as [CommsFilter, string, number][]).map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={
              "px-3 py-1.5 text-sm rounded-lg font-medium transition flex items-center gap-1.5 " +
              (filter === key
                ? "bg-bardin-teal text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:border-bardin-teal/30")
            }
          >
            {label}
            {count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                filter === key ? "bg-white/20" : "bg-slate-100"
              }`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Timeline + Load more */}
      <CommsTimelineList
        entries={filtered}
        emptyLabel={`No ${filter === "all" ? "communications" : filter} found for this deal.`}
      />
    </div>
  );
}
